import { IconDefinition, IconProp } from '@fortawesome/fontawesome-svg-core';
import { faPlus, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React from 'react';
import { HelperPopup } from '../../HelperPopup';

type IconButtonProps = {
  /**
   * Icon to display in the button - can be FontAwesome IconDefinition or React element
   */
  icon?: IconDefinition | React.ReactElement;
  /**
   * Function to call when button is clicked
   */
  onClick: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Additional classes to apply to the button
   */
  className?: string;
  /**
   * Size of the button (medium or large)
   */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /**
   * Whether the button is disabled
   */
  disabled?: boolean;
  /**
   * Type of the button (defaults to button)
   */
  type?: 'button' | 'close';
  /**
   * Variant of the button styling
   */
  variant?: 'default' | 'outlined' | 'primary';
  /**
   * ARIA label for accessibility
   */
  ariaLabel?: string;
  /**
   * Optional tooltip text to show on hover
   */
  tooltip?: string;
  /**
   * Whether the button is toggled
   */
  toggled?: boolean;
};

export const IconButton: React.FC<IconButtonProps> = ({
  icon = faPlus,
  onClick,
  className,
  size = 'md',
  disabled = false,
  type = 'button',
  variant = 'default',
  ariaLabel,
  tooltip,
  toggled = false,
}) => {
  const iconSize =
    size === 'xs'
      ? 'h-3 w-3'
      : size === 'sm'
      ? 'h-5 w-5'
      : size === 'md'
      ? 'h-6 w-6'
      : 'h-7 w-7 lg:h-8 lg:w-8';
  const buttonSize =
    size === 'xs' || size === 'sm' || size === 'md' ? 'h-10 w-10' : 'h-12 w-12';

  // Define base and variant-specific styles
  const baseStyles =
    'duration-250 flex items-center justify-center transition-all';
  const variantStyles = {
    default:
      'text-secondary hover:bg-gray-100 hover:text-primary rounded-full active:bg-gray-200',
    outlined:
      'text-secondary border border-outline-variant bg-white rounded-full hover:bg-surface-low hover:text-primary hover:border-gray-400 active:bg-surface-high',
    primary:
      'text-white bg-brand rounded-full hover:bg-brand-darker active:bg-brand-darker',
  };

  const button = (
    <button
      type='button'
      onClick={(event) => onClick(event)}
      disabled={disabled}
      aria-label={ariaLabel}
      className={classNames(
        baseStyles,
        variantStyles[variant],
        buttonSize,
        {
          'cursor-not-allowed opacity-50': disabled,
        },
        {
          '!border-brand !bg-brand-ghost !text-brand': toggled,
        },
        className
      )}
    >
      {type === 'close' ? (
        <FontAwesomeIcon
          icon={faXmark as IconProp}
          className={classNames(iconSize, 'lg:h-6 lg:w-6')}
        />
      ) : React.isValidElement(icon) ? (
        // Render React element directly with proper className handling
        <div className={iconSize}>{icon}</div>
      ) : (
        <FontAwesomeIcon icon={icon as IconDefinition} className={iconSize} />
      )}
    </button>
  );

  // If tooltip is provided, wrap with HelperPopup
  if (tooltip) {
    return <HelperPopup icon={button}>{tooltip}</HelperPopup>;
  }

  // Return button without tooltip
  return button;
};
